import { z } from 'zod';
import { paginatedSchema } from './pagination';
import { membershipPackageSchema } from './payment';
import { bidSchema, lotStateSchema, saleStateSchema } from './sale';

/**
 * Bidder-web (M7) DTOs — the shapes the public showcase + bidder app need that earlier
 * milestones did not build (`docs/04-api-contract.md`, ADR 0020). The **public auction card**
 * and the **login-gated auction detail** deliberately reuse the existing `saleSummarySchema`
 * / `saleDetailSchema` (the detail's manager-only fields — numeric reserve, barem, conference
 * host URL — are already optional there and omitted at runtime by
 * `toSaleDetail(..., { privileged:false, conferenceView:false })`), so only the genuinely new
 * shapes live here: self-registration (with KVKK consent), the "my bids" row, and the public
 * membership tier. Money is integer kuruş; timestamps are epoch-ms integers.
 */

// --- POST /auth/register — bidder self-registration + KVKK consent ----------

/**
 * Public self-registration. Always creates a plain **USER** (the granted role is never taken
 * from the body — no escalation); a HELD deposit or a membership purchase later grants
 * bidding. Turnstile-guarded (bot/abuse). **KVKK consent is mandatory**: both the Aydınlatma
 * Metni and the Açık Rıza Metni must be accepted (`z.literal(true)` — a false/missing flag is
 * a 400), and the accepted document `version` is captured so a later copy revision is
 * distinguishable (the append-only `kvkk_consents` table records one row per document).
 */
export const registerSchema = z.object({
  email: z.email(),
  password: z.string().min(8).max(200),
  nameFirst: z.string().min(1).max(100),
  nameLast: z.string().min(1).max(100),
  mobile: z.string().max(30).optional(),
  locale: z.enum(['tr', 'en']).optional(), // preferred notification language (M8); defaults to 'tr'
  turnstileToken: z.string().min(1),
  consent: z.object({
    aydinlatma: z.literal(true), // KVKK Aydınlatma Metni — must be accepted
    acikRiza: z.literal(true), // KVKK Açık Rıza Metni — must be accepted
    version: z.string().min(1).max(40), // the accepted document-set version
  }),
});
export type RegisterInput = z.infer<typeof registerSchema>;

/**
 * `POST /auth/register` response (M15). Registration deliberately returns **no tokens**: the
 * account lands in the approval queue, so there is no session to hand out yet. This is a
 * distinct shape rather than a nullable `TokenResponse` so the client cannot accidentally
 * treat a successful application as a login — the bidder app parses the body and would
 * otherwise surface a *successful* registration as a schema error.
 */
export const registrationResultSchema = z.object({
  status: z.literal('pending'),
});
export type RegistrationResult = z.infer<typeof registrationResultSchema>;

// --- GET /me/bids — the caller's cross-sale bid history ----------------------

/**
 * One row of the bidder's own bid history: the {@link bidSchema} enriched with the sale +
 * lot reference the "My bids" page renders. Scoped to the caller (only their own bids).
 */
export const myBidRowSchema = bidSchema.extend({
  saleId: z.uuid(),
  saleCode: z.string(),
  saleTitle: z.string().nullable(),
  plateSnapshot: z.string().nullable(),
  currency: z.string(), // the lot's currency (lots can differ, e.g. EUR) — format per-bid
  /**
   * The sale's lifecycle state, so the page can offer a way **back to the car** while there
   * is still something to do there. A row for a closed auction is history; a row for a
   * running (or not-yet-started, i.e. early-bid) one is a lot the bidder may still act on.
   */
  saleState: saleStateSchema,
});
export type MyBidRow = z.infer<typeof myBidRowSchema>;

/** Paginated `/me/bids` response — the `{ items, total }` envelope over {@link myBidRowSchema}. */
export const myBidsPageSchema = paginatedSchema(myBidRowSchema);
export type MyBidsPage = z.infer<typeof myBidsPageSchema>;

// --- GET /me/stats — the bidder dashboard's counters ------------------------

/**
 * The signed-in bidder's own participation counters, for the dashboard at `/me` (Bia
 * feedback, 11.08.2026: "let him see his own history — how many he entered, how many he
 * struck, whether he got one or not"). Every figure is scoped to the caller.
 *
 * Two shape decisions, mirroring {@link import('./sale').saleStatsSchema}:
 * - **Raw counts, bucketed by the client.** `lots` is a per-state map rather than a
 *   pre-computed won/lost split, so adding or renaming a lot state does not break this
 *   contract — the page decides which states read as decided, pending or neither.
 * - **Every state present with an explicit `0`.** The dashboard renders a fixed tile grid; an
 *   absent key there would read as "unknown" rather than "none".
 *
 * Deliberately counters only: conversion rates and spend-over-time belong to the P2 analytics
 * dashboard (`docs/01-prd.md` non-goals), not here.
 */
export const myStatsSchema = z.object({
  /** Distinct auctions the caller has bid in. */
  sales: z.number().int(),
  /** Bids the caller has placed, all time (every bid, not distinct lots). */
  bids: z.number().int(),
  /**
   * Distinct lots the caller has bid on, keyed by the lot's **current** state. `SOLD` includes
   * the ones they won — {@link myStatsSchema.won} is the subset, so "lost" is
   * `SOLD + UNSOLD − won` and a `REMOVED` lot counts as neither (a withdrawn lot was never a
   * contest).
   */
  lots: z.record(lotStateSchema, z.number().int()),
  /** Lots the caller won: `SOLD` with the caller as `winnerUserId`. */
  won: z.number().int(),
  /**
   * `OPEN` lots where the caller currently holds the top bid. Point-in-time — it moves the
   * moment someone outbids them, and the dashboard does not subscribe to the live socket.
   */
  leading: z.number().int(),
});
export type MyStats = z.infer<typeof myStatsSchema>;

// --- GET /memberships — bidder-facing catalog (purchase page) ---------------

/**
 * A purchasable membership tier as shown to a bidder — the active subset of the admin catalog
 * ({@link membershipPackageSchema}) without the `active` flag (the list already contains only
 * active tiers). The bidder never sees the granted-role plumbing beyond what's needed to
 * render + purchase.
 */
export const publicMembershipTierSchema = membershipPackageSchema.pick({
  id: true,
  name: true,
  amount: true,
  role: true,
});
export type PublicMembershipTier = z.infer<typeof publicMembershipTierSchema>;

// --- Push notifications (M8, ADR 0021) — subscription registration ----------

/**
 * A browser Web Push subscription (the JSON `pushManager.subscribe()` yields), stored per user
 * in `users.web_push_sub`. `keys.p256dh`/`keys.auth` are base64url; the send path
 * (`lib/notifications/web-push.ts`) encrypts with them per RFC 8291. The keys are **bounded and
 * format-checked** (base64url, near the exact byte sizes — p256dh is a 65-byte P-256 point ≈ 88
 * chars, auth is 16 bytes ≈ 24 chars) so a caller can't persist megabyte blobs that the digest
 * would later base64-decode and feed to WebCrypto.
 */
/**
 * A base64url string that encodes exactly `bytes` bytes. Unpadded base64url of N bytes has a fixed
 * length `ceil(N*4/3)` (e.g. 65→87, 16→22), so an exact length check validates the decoded size
 * without needing `atob` (not in this package's lib types) — rejecting both too-short values like
 * `AAAA` and oversized blobs.
 */
const base64urlBytes = (bytes: number, label: string) => {
  const len = Math.ceil((bytes * 4) / 3);
  return z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/, `${label} must be base64url`)
    .length(len, `${label} must encode exactly ${bytes} bytes`);
};

export const pushSubscriptionSchema = z.object({
  endpoint: z.url().max(2048),
  keys: z.object({
    // p256dh is a 65-byte uncompressed P-256 point; auth is a 16-byte secret (RFC 8291).
    p256dh: base64urlBytes(65, 'p256dh'),
    auth: base64urlBytes(16, 'auth'),
  }),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

/** An FCM registration token (mobile), stored per user in `users.fcm_token`. */
export const fcmTokenSchema = z.object({
  token: z.string().min(1).max(4096),
});
export type FcmTokenInput = z.infer<typeof fcmTokenSchema>;

/** The caller's preferred notification language (M8) — kept in sync with the UI language switch. */
export const localeSchema = z.object({
  locale: z.enum(['tr', 'en']),
});
export type LocaleInput = z.infer<typeof localeSchema>;

/**
 * Public notification config the client needs to subscribe — the VAPID application server key.
 * `null` when web push is not configured on the server (the client then hides the opt-in).
 */
export const notificationsConfigSchema = z.object({
  vapidPublicKey: z.string().nullable(),
});
export type NotificationsConfig = z.infer<typeof notificationsConfigSchema>;
